import { Copy, Loader2, ExternalLink, Check, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { toast } from "sonner";
import type { EmbedMode, Event, UpdateEventRequest } from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useShopsQuery } from "@/features/shops/api";
import { type Device, DeviceToggle } from "@/features/shops/shop-preview-card";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

import { useUpdateEventMutation } from "./api";
import { isoToLocalInput, localInputToIso } from "./date-helpers";

interface Props {
  slug: string;
  evt: Event;
}

/**
 * Wrapper: parent prop'ları değiştiğinde inner state'i sıfırlamak için
 * `key` ile remount ediyoruz. Aksi takdirde "props → state senkronu"
 * için `useEffect` kullanmak gerekirdi ki `react-hooks/set-state-in-effect`
 * lint kuralı bunu reddediyor. Remount stratejisi de aynı sonucu daha
 * temiz veriyor: backend taze veri döndüğünde form alanları kendiliğinden
 * yenilenir.
 */
export function EventEmbedPanel(props: Props) {
  const { evt } = props;
  const stateKey = `${evt.id}|${evt.embedEnabled}|${evt.embedSlug ?? ""}|${evt.embedMode}`;
  return <EventEmbedPanelInner key={stateKey} {...props} />;
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

type SlugError = "length" | "pattern" | "taken";

const EMBED_LOCALES = ["", "tr", "en", "ar"] as const;
type EmbedLocale = (typeof EMBED_LOCALES)[number];

const CODE = <span className="font-mono" />;

/**
 * Event detail sayfasındaki "Embed" sekmesi. Üç ana iş yapıyor:
 *   1. Embed iframe açık/kapalı toggle.
 *   2. Custom embed slug input (organizer kendi sitesinde okunabilir URL ister).
 *   3. Default mod seçici (Compact = teaser, Inline = ticket selector hep açık).
 *
 * Ayrıca canlı snippet preview + copy-to-clipboard buton + iframe önizleme.
 * Birden fazla shop varsa snippet'in shop slug parametresini değiştirebilmek
 * için minimal bir shop selector da burada — iframe URL'i {shop}/{event}
 * pattern'iyle çalıştığı için organizer hangi shop kanalıyla embed
 * göstereceğine karar vermeli.
 *
 * PUT (PATCH değil) sebebi: Mevcut backend endpoint'i full update; biz
 * elimizdeki event verisinin tüm alanlarını payload'a ekleyip embed
 * alanlarını override ediyoruz. Avantajı: dashboard tarafında ek backend
 * endpoint yok. Dezavantajı: Concurrent edit window olursa son kaydeden
 * kazanır — MVP'de organizer tek kullanıcı olduğu için kabul edilebilir.
 */
function EventEmbedPanelInner({ slug, evt }: Props) {
  const t = useT("eventEmbed");
  const tCommon = useT("common");
  const mutation = useUpdateEventMutation(slug);
  const shopsQuery = useShopsQuery();

  const usableShops = useMemo(() => {
    return (shopsQuery.data ?? []).filter((s) => s.status !== "Archived");
  }, [shopsQuery.data]);

  const [enabled, setEnabled] = useState(evt.embedEnabled);
  const [mode, setMode] = useState<EmbedMode>(evt.embedMode);
  const [embedSlug, setEmbedSlug] = useState(evt.embedSlug ?? "");
  const [slugError, setSlugError] = useState<SlugError | null>(null);
  // Initial seçim olarak ilk archived olmayan shop. Shop listesi async geldiği
  // için boş başlayabilir; aşağıda fallback `usableShops[0]?.slug` kullanılıyor.
  const [previewShopSlug, setPreviewShopSlug] = useState<string>("");
  // Boş = ziyaretçinin dili; sadece koda yazılır, etkinliğe kaydedilmez.
  const [embedLocale, setEmbedLocale] = useState<EmbedLocale>("");

  const effectiveShopSlug = previewShopSlug || usableShops[0]?.slug || "";

  const effectiveSlug = (embedSlug.trim() || evt.slug).toLowerCase();

  const storefrontBaseUrl =
    import.meta.env.VITE_SHOP_BASE_URL ?? "http://localhost:3002";

  const snippet = useMemo(() => {
    const shop = effectiveShopSlug || "MAGAZA-SLUG";
    const modeAttr = mode === "Compact" ? "compact" : "inline";
    return [
      `<div`,
      `  data-ticketnjoy-shop="${shop}"`,
      `  data-ticketnjoy-event="${effectiveSlug}"`,
      `  data-ticketnjoy-mode="${modeAttr}"`,
      ...(embedLocale ? [`  data-ticketnjoy-locale="${embedLocale}"`] : []),
      `></div>`,
      `<script src="${storefrontBaseUrl}/v1/embed.js" async></script>`,
    ].join("\n");
  }, [effectiveSlug, mode, effectiveShopSlug, storefrontBaseUrl, embedLocale]);

  const previewQuery = new URLSearchParams({
    mode: mode === "Compact" ? "compact" : "inline",
    ...(embedLocale ? { locale: embedLocale } : {}),
  }).toString();
  // Kaydedilmemiş embed adı henüz çözülmez; önizleme kayıtlı adı kullanır.
  const savedSlug = evt.embedSlug ?? evt.slug;
  const previewUrl = effectiveShopSlug
    ? `${storefrontBaseUrl}/embed/${effectiveShopSlug}/${savedSlug}?${previewQuery}`
    : null;

  const validateSlug = (raw: string): SlugError | null => {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    if (trimmed.length < 2 || trimmed.length > 100) return "length";
    if (!SLUG_PATTERN.test(trimmed)) return "pattern";
    return null;
  };

  const onSlugBlur = () => {
    const trimmed = embedSlug.trim().toLowerCase();
    if (trimmed !== embedSlug) setEmbedSlug(trimmed);
    setSlugError(validateSlug(trimmed));
  };

  const handleSave = () => {
    const trimmed = embedSlug.trim().toLowerCase();
    const err = validateSlug(trimmed);
    if (err) {
      setSlugError(err);
      return;
    }

    // Mevcut event'in tüm field'larını payload'a kopyala — backend PUT full
    // update; eksik alan null'a düşürür. Bu wrapper sadece embed alanlarını
    // override ediyor.
    const payload: UpdateEventRequest = {
      title: evt.title,
      description: evt.description,
      coverImageUrl: evt.coverImageUrl,
      venueName: evt.venueName,
      venueAddress: evt.venueAddress,
      venueCity: evt.venueCity,
      latitude: evt.latitude,
      longitude: evt.longitude,
      startsAt: localInputToIso(isoToLocalInput(evt.startsAt))!,
      endsAt: localInputToIso(isoToLocalInput(evt.endsAt))!,
      defaultTaxRate: evt.defaultTaxRate,
      embedEnabled: enabled,
      embedSlug: trimmed.length > 0 ? trimmed : null,
      embedMode: mode,
    };

    mutation.mutate(payload, {
      onSuccess: () => toast.success(t("saved")),
      onError: (e) => {
        const msg = e.message;
        if (msg.toLowerCase().includes("embed_slug_taken")) {
          setSlugError("taken");
        } else {
          toast.error(msg);
        }
      },
    });
  };

  const dirty =
    enabled !== evt.embedEnabled ||
    mode !== evt.embedMode ||
    embedSlug.trim() !== (evt.embedSlug ?? "");

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-base font-semibold">{t("heading")}</h2>
        <p className="text-xs text-muted-foreground">{t("description")}</p>
      </div>

      <div className="rounded-lg border p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="embed-enabled" className="text-sm font-medium">
              {t("enabled")}
            </Label>
            <p className="text-xs text-muted-foreground">{t("enabledHint")}</p>
          </div>
          <Switch
            id="embed-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>
      </div>

      <div className="rounded-lg border p-4">
        <Label htmlFor="embed-slug" className="text-sm font-medium">
          {t("slug")}
        </Label>
        <p className="mt-1 text-xs text-muted-foreground">
          <Trans
            i18nKey="eventEmbed.slugHint"
            values={{ slug: evt.slug }}
            components={{ code: CODE }}
          />
        </p>
        <Input
          id="embed-slug"
          value={embedSlug}
          onChange={(e) => {
            setEmbedSlug(e.target.value);
            if (slugError) setSlugError(null);
          }}
          onBlur={onSlugBlur}
          placeholder={evt.slug}
          className="mt-3 font-mono"
          dir="ltr"
          aria-invalid={Boolean(slugError)}
        />
        {slugError ? (
          <p className="mt-1 text-xs text-destructive">{t(`slugErrors.${slugError}`)}</p>
        ) : null}
      </div>

      <div className="rounded-lg border p-4">
        <Label className="text-sm font-medium">{t("mode")}</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          <Trans i18nKey="eventEmbed.modeHint" components={{ code: CODE }} />
        </p>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {(["Compact", "Inline"] as const).map((m) => (
            <ModeOption
              key={m}
              value={m}
              currentValue={mode}
              onChange={setMode}
              label={t(`modes.${m}.title`)}
              description={t(`modes.${m}.description`)}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={handleSave}
          disabled={!dirty || mutation.isPending || Boolean(slugError)}
        >
          {mutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
          {tCommon("save")}
        </Button>
        {dirty ? <span className="text-xs text-muted-foreground">{t("unsaved")}</span> : null}
      </div>

      <SnippetCard
        snippet={snippet}
        previewUrl={previewUrl}
        shops={usableShops.map((s) => ({ slug: s.slug, name: s.name }))}
        selectedShopSlug={effectiveShopSlug}
        onShopChange={setPreviewShopSlug}
        locale={embedLocale}
        onLocaleChange={setEmbedLocale}
        embedDisabled={!evt.embedEnabled}
      />

      {previewUrl ? (
        <LivePreview src={previewUrl} savedKey={`${evt.updatedAt}`} />
      ) : null}
    </div>
  );
}

function ModeOption({
  value,
  currentValue,
  onChange,
  label,
  description,
}: {
  value: EmbedMode;
  currentValue: EmbedMode;
  onChange: (m: EmbedMode) => void;
  label: string;
  description: string;
}) {
  const isActive = value === currentValue;
  return (
    <button
      type="button"
      onClick={() => onChange(value)}
      className={`flex flex-col gap-1 rounded-md border p-3 text-start transition ${
        isActive
          ? "border-primary bg-primary/5"
          : "hover:border-foreground/30 hover:bg-muted/40"
      }`}
    >
      <span className="flex items-center gap-2 text-sm font-medium">
        {label}
        {isActive ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
      </span>
      <span className="text-xs text-muted-foreground">{description}</span>
    </button>
  );
}

function SnippetCard({
  snippet,
  previewUrl,
  shops,
  selectedShopSlug,
  onShopChange,
  locale,
  onLocaleChange,
  embedDisabled,
}: {
  snippet: string;
  previewUrl: string | null;
  shops: { slug: string; name: string }[];
  selectedShopSlug: string;
  onShopChange: (slug: string) => void;
  locale: EmbedLocale;
  onLocaleChange: (locale: EmbedLocale) => void;
  embedDisabled: boolean;
}) {
  const t = useT("eventEmbed");
  const tCommon = useT("common");
  const tLocale = useT("locale");
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(tCommon("copyFailed"));
    }
  };

  return (
    <div className="rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">{t("snippetTitle")}</h3>
          <p className="text-xs text-muted-foreground">{t("snippetDescription")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleCopy}
            disabled={!selectedShopSlug}
          >
            {copied ? (
              <Check className="me-1.5 h-3.5 w-3.5" />
            ) : (
              <Copy className="me-1.5 h-3.5 w-3.5" />
            )}
            {copied ? tCommon("copied") : tCommon("copy")}
          </Button>
          {previewUrl ? (
            <Button type="button" size="sm" variant="ghost" asChild>
              <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="me-1.5 h-3.5 w-3.5" />
                {t("preview")}
              </a>
            </Button>
          ) : null}
        </div>
      </div>

      {shops.length > 1 ? (
        <div className="mt-3">
          <Label htmlFor="embed-shop" className="text-xs font-medium">
            {t("shop")}
          </Label>
          <select
            id="embed-shop"
            value={selectedShopSlug}
            onChange={(e) => onShopChange(e.target.value)}
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {shops.map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.name} ({s.slug})
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">{t("shopHint")}</p>
        </div>
      ) : null}

      <div className="mt-3">
        <Label htmlFor="embed-locale" className="text-xs font-medium">
          {t("locale")}
        </Label>
        <select
          id="embed-locale"
          value={locale}
          onChange={(e) => onLocaleChange(e.target.value as EmbedLocale)}
          className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {EMBED_LOCALES.map((l) => (
            <option key={l || "auto"} value={l}>
              {l ? tLocale(l) : t("localeAuto")}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-muted-foreground">{t("localeHint")}</p>
      </div>

      <Textarea
        readOnly
        rows={6}
        value={snippet}
        dir="ltr"
        className="mt-3 font-mono text-xs"
        onFocus={(e) => e.target.select()}
      />

      {embedDisabled ? (
        <p className="mt-2 text-xs text-warning-foreground">
          <Trans
            i18nKey="eventEmbed.disabledWarning"
            components={{ b: <span className="font-medium" /> }}
          />
        </p>
      ) : null}
    </div>
  );
}

/**
 * Kaydedilmiş embed'in iframe önizlemesi (`tn_preview=1` → ödeme kapalı).
 * Yüksekliği gerçek loader gibi `tn:resize` mesajıyla izler.
 */
function LivePreview({ src, savedKey }: { src: string; savedKey: string }) {
  const t = useT("eventEmbed");
  const [device, setDevice] = useState<Device>("desktop");
  const [reloadKey, setReloadKey] = useState(0);
  const [height, setHeight] = useState(360);
  const frameRef = useRef<HTMLIFrameElement>(null);

  const url = new URL(src);
  url.searchParams.set("tn_preview", "1");
  const origin = url.origin;

  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      if (ev.source !== frameRef.current?.contentWindow || ev.origin !== origin) return;
      const data = ev.data;
      if (data?.source !== "ticketnjoy-embed" || data.type !== "tn:resize") return;
      if (typeof data.height === "number") setHeight(Math.max(200, data.height + 8));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [origin]);

  return (
    <div className="rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">{t("livePreview")}</h3>
          <p className="text-xs text-muted-foreground">{t("livePreviewHint")}</p>
        </div>
        <div className="flex items-center gap-2">
          <DeviceToggle value={device} onChange={setDevice} />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setReloadKey((k) => k + 1)}
            title={t("reload")}
            aria-label={t("reload")}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="mt-3 flex justify-center rounded-md border bg-muted/30 p-3">
        <iframe
          key={`${savedKey}-${reloadKey}-${url.toString()}`}
          ref={frameRef}
          src={url.toString()}
          title={t("livePreview")}
          className={cn("rounded-md bg-background", device === "desktop" && "w-full")}
          style={{
            width: device === "mobile" ? 375 : 640,
            height,
            maxWidth: "100%",
          }}
        />
      </div>
    </div>
  );
}
